import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class BrowserUtilityService {
  browserMetaData: any;
  metadataKeyMap: any;
  constructor(private sanitizer: DomSanitizer) {}

  getMetadataProperty(model: string, field: string, code: string, hasLanguageTitle = false): string {
    if (this.metadataKeyMap) {
      const key = `${model}.${field}.${code}`;
      if (hasLanguageTitle && this.metadataKeyMap[key]) {
        return `${this.metadataKeyMap[key]} (Preferred name for this type and language)`;
      } else if (hasLanguageTitle) {
        return 'Preferred name for this type and language';
      }
      return this.metadataKeyMap[key];
    }
    return '';
  }

  getBrowserUiLabel(publisher: string, terminology: string, version: string, model: string, label: string) {
    if (this.browserMetaData) {
      return (
        this.browserMetaData?.find(
          (item: any) =>
            item?.publisher === publisher &&
            item?.terminology === terminology &&
            item?.version === version &&
            item?.model === model &&
            item?.field === 'uiLabel',
        )?.code ?? label
      );
    } else {
      return label;
    }
  }

  escapeSafeSearch(code: string): string {
    const regCharacters = ['\\', '+', '-', '!', '(', ')', ':', '^', '[', ']', '"', '{', '}', '~', '*', '?', '|', '&', '/'];
    const escapeCharacters = ['\\\\', '\\+', '-', '!', '\\(', '\\)', ':', '^', '\\[', '\\]', '"', '\\{', '\\}', '~', '*', '?', '|', '&', '/'];
    for (let i = 0; i < regCharacters.length; i++) {
      if (code.includes(regCharacters[i])) {
        const regEx = new RegExp(escapeCharacters[i], 'g');
        code = code.replace(regEx, `\\${regCharacters[i]}`);
      }
    }
    return code;
  }

  //highlighting functions from cihi

  highlightText(text: any, pattern: string): SafeHtml {
    if (text) {
      return text.replace(new RegExp(pattern, 'igm'), '<span class="highlight">$&</span>');
    }
    return '';
  }

  highlightAllText(text: any, pattern: string): SafeHtml {
    if (text) {
      return text.replace(new RegExp(`^(${pattern})([A-Z-.^]*$)`, 'igm'), '<span class="highlight">$1</span>$2');
    }
    return '';
  }

  public getCodeQuery(code: string): string {
    if (!code) {
      return '';
    }
    const trimcode: string = code.trim().toLowerCase();

    const pattern1 = new RegExp(/^[A-Za-z]\*?$/);
    const pattern2 = new RegExp(/^[A-Za-z]\d\*?$/);
    const pattern3 = new RegExp(/^[A-Za-z]\d\d\*?$/);
    const pattern4 = new RegExp(/^[A-Za-z]\d\d[\d\. *]+$/);
    const pattern5 = new RegExp(/^\d{2}$/);
    const pattern6 = new RegExp(/^[A-Za-z](\d\d|\d\*|\*)\-[A-Za-z](\d\d|\d\*|\*)$/);

    if (pattern1.test(trimcode) || pattern2.test(trimcode) || pattern3.test(trimcode)) {
      return trimcode.replace(/\*+$/g, '');
    } else if (pattern4.test(trimcode)) {
      const part1 = trimcode.replace(/([A-Za-z]\d\d)([\d\. *]*)/, '$1');
      const part2 = trimcode
        .replace(/([A-Za-z]\d\d)([\d\. *]*)/, '$2')
        .replace(/ {2,}/g, '.')
        .replace(/\*+/g, '');
      const combined = part1 + part2;
      return combined.replace(/\.\.+/g, '.');
    } else if (pattern5.test(trimcode)) {
      return trimcode;
    } else if (pattern6.test(trimcode)) {
      return trimcode.replace(/-/, '\\-').replace(/\*+$/g, '');
    }

    return '';
  }

  public escapeRegexpWords(text: string, splitOnSpace: boolean = true): string {
    if (!text) {
      return '';
    }
    const spacePattern: RegExp = splitOnSpace ? /\s+/ : /abcdef/;
    const words = new Set(
      text
        // daggers,*,',",(),[], {} -
        // NOTE: not -, so that "C00-C97" works (for code ranges)
        .replace(/['"\(\)\{\}\{\}]/g, ' ')
        // replace these characters at the end of the line
        .replace(/[,\.\u2020*]$/, ' ')
        .trim()
        // But something like non-responsiveness should have the dash replaced (does not affect codes)
        .replace(/([a-z0-9])-([a-z0-9])/g, '$1 $2')
        .split(spacePattern)
        .filter((word) => word.length > 0),
    );
    const patterns = [];

    words.forEach((word) => {
      // removed \\ because we are otherwise escaping and want to keep it.
      const escapedWord = word.replace(/[.*+?^${}()|[\]]/g, '\\$&');
      patterns.push(escapedWord);
    });
    return patterns.join('|');
  }

  getHighlights(element: any, expression: string, full = true) {
    if (element?.length > 0 && typeof element === 'object') {
      element.forEach((item) => {
        if (full) {
          item.highlightcode = this.highlightAllText(item.code, expression);
          item.highlightname = this.highlightAllText(item.name, expression);
        } else {
          item.highlightcode = this.highlightText(item.code, expression);
          item.highlightname = this.highlightText(item.name, expression);
        }
        item.highlightcode = this.sanitizer.bypassSecurityTrustHtml(item.highlightcode);
        item.highlightname = this.sanitizer.bypassSecurityTrustHtml(item.highlightname);

        let parent = item.parent;
        let title = item.name;
        while (parent) {
          if (parent.name.length == 1) {
            break;
          }
          title = parent.name + (title ? ' -> ' : '') + title;
          parent = parent.parent;
        }

        if (title !== item.name) {
          item.title = title;
        }
      });
    }
    if (typeof element === 'string') {
      if (full) {
        element = this.highlightAllText(element, expression);
      } else {
        element = this.highlightText(element, expression);
      }
      element = this.sanitizer.bypassSecurityTrustHtml(element);
      return element;
    }
  }
}
